//@flow
import React, {Component} from 'react';
import {StyleSheet, View, Platform, LayoutAnimation} from 'react-native';
import {IconBadge, Text, Touchable} from 'trpc-react-native-ui';
import {DateTimeLabel, Icon} from '../index';
import ListViewRowFooter from './ListViewRowFooter';
import theme from '../../../theme-config';
import I18n from '../../../lib/i18n';

class ListViewRow extends Component<Props> {
  styles = getStyles();

  UNSAFE_componentWillUpdate(nextProps: Props) {
    const {folded = true} = this.props;
    if (nextProps.folded === folded) {
      return;
    }

    const animation =
      Platform.OS === 'android'
        ? {
            duration: 200,
            update: {
              type: 'easeInEaseOut',
            },
          }
        : {
            duration: 600,
            update: {
              type: 'spring',
              springDamping: 0.7,
            },
          };

    LayoutAnimation.configureNext(animation);
  }

  render() {
    const styles = this.styles;
    const foldable =
      this.props.children &&
      ((Array.isArray(this.props.children) &&
        this.props.children.filter(Boolean).length) ||
        !Array.isArray(this.props.children));

    const {folded = true} = this.props;

    const MaybeTouchable =
      foldable &&
      this.props.onToggle &&
      typeof this.props.onToggle === 'function'
        ? ({children}: {children: any}) => (
            <Touchable onPress={this.props.onToggle}>{children}</Touchable>
          )
        : View;
    return (
      <MaybeTouchable>
        <View style={[styles.container, !foldable && {paddingBottom: 25}]}>
          <View style={styles.cornerContainer}>
            <DateTimeLabel
              timestamp={this.props.timestamp}
              style={styles.cornerText}
            />
          </View>
          <View style={styles.innerContainer}>
            <View style={styles.startContainer}>
              {this.props.noBadge ? (
                <Icon
                  color={this.props.color}
                  name={this.props.iconName}
                  size={30}
                  style={{
                    transform: [
                      {scaleX: I18n.rtl() && this.props.reverseIcon ? -1 : 1},
                    ],
                  }}
                />
              ) : (
                <View style={styles.iconBadge}>
                  <IconBadge
                    iconName={this.props.iconName}
                    backgroundColor={this.props.color}
                    dimension={40}
                    iconSize={20}
                    reverse={this.props.reverseIcon}
                  />
                </View>
              )}
              <View style={styles.titlesContainer}>
                <Text style={styles.title}>{this.props.title}</Text>
                {(this.props.subtitle || this.props.subtitleDetail) && (
                  <View style={styles.subtitles}>
                    <Text style={this.props.subtitleStyle || styles.subtitle}>
                      {this.props.subtitle}
                    </Text>
                    {this.props.subtitle && this.props.subtitleDetail ? (
                      <Text style={styles.subtitle}> - </Text>
                    ) : null}
                    <Text style={this.props.subtitleStyle || styles.subtitle}>
                      {this.props.subtitleDetail}
                    </Text>
                  </View>
                )}
              </View>
            </View>
            {!!this.props.endText && (
              <View style={styles.endTextContainer}>
                <Text style={[styles.endText, {color: theme.primaryColor}]}>
                  {this.props.endText}
                </Text>
              </View>
            )}
          </View>
          {!folded && (
            <View style={styles.childrenContainer}>{this.props.children}</View>
          )}
          {!!foldable && <ListViewRowFooter folded={folded} />}
        </View>
      </MaybeTouchable>
    );
  }
}

function getStyles() {
  return StyleSheet.create({
    childrenContainer: {
      marginHorizontal: 15,
      marginTop: 10,
    },
    container: {
      backgroundColor: 'white',
    },
    cornerContainer: {
      marginTop: 8,
      marginRight: 12,
      alignItems: 'flex-end',
    },
    cornerText: {
      fontSize: 13,
      color: 'grey',
    },
    endText: {
      fontWeight: '600',
      textAlign: 'right',
    },
    endTextContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: 10,
      marginTop: 5,
    },
    iconBadge: {
      marginHorizontal: 10,
    },
    innerContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 5,
      marginHorizontal: 5,
    },
    startContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
    },
    subtitle: {
      color: theme.gray.normal,
      fontSize: 13,
    },
    subtitles: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    title: {
      fontWeight: 'bold',
    },
    titlesContainer: {
      flex: 1,
      marginHorizontal: 5,
      justifyContent: 'space-around',
    },
  });
}

type Props = {
  noBadge?: boolean,
  title: string,
  subtitle?: string,
  subtitleDetail?: string,
  endText?: string,
  iconName: string,
  color: string,
  timestamp: number,
  folded?: boolean,
  onToggle?: Function,
  children: any,
  reverseIcon?: boolean,
  subtitleStyle?: Object,
};

export default ListViewRow;
